'use client'

import Link from 'next/link'
import { ArrowLeft } from '@carbon/icons-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import {
  EASE,
  Eyebrow,
  HAIRLINE,
  QuietLink,
  useReveal,
} from '@/components/page-kit'

/* ─── Page device: the transfer manifest ───────────────────────────────────
   A migration is a manifest — a numbered list of source systems and, against
   each one, exactly what crosses over. So the page is built as one: ordinals
   hanging in a left margin, hairline rules between entries, and each system's
   own mark sitting at the end of its row like a letterhead.

   What this replaces: eight full-width alternating image/text blocks, each
   about 700px tall, which made a 7,375px page out of the same layout repeated
   eight times. The entries carry identical copy in a fraction of the height,
   and the reader can compare them because they are now a list rather than
   eight consecutive heroes.

   Also removed: seven vendor logos hotlinked straight from upload.wikimedia.org
   and eight product screenshots each buried under a 92% black scrim with a
   pulsing capsule badge printed on top; the tinted pill badges, the
   icon-in-a-box process cards, the glow on the progress bar, and
   `text-justify` on every description.

   The marks are now local SVGs under /public/logos, taken from svgl.app and
   downloaded rather than hotlinked. Every word of copy is unchanged. ── */

interface System {
  slug: string
  category: string
  /** Local logo. `invert` flips dark-drawn art white in dark mode; `light`/
   *  `dark` is a pair for marks whose wordmark changes colour per theme. */
  logo?: { src: string; alt: string; invert?: boolean }
  logoPair?: { light: string; dark: string; alt: string }
  title: string
  subtitle: string
  description: string
  points: string[]
}

const SYSTEMS: System[] = [
  {
    slug: 'google-cloud',
    category: 'AI & Analytics',
    logo: { src: '/logos/google-cloud.svg', alt: 'Google Cloud' },
    title: 'Google Cloud Platform',
    subtitle: 'Scalable ML infrastructure powering predictive intelligence',
    description:
      "Google Cloud underpins Euclideum's AI layer turning raw operational data into forward-looking organisational intelligence. BigQuery processes millions of records in seconds; Vertex AI models surface risk signals and outcome forecasts before they appear in conventional reports.",
    points: [
      'BigQuery warehousing for cross-module analytics and BI dashboards',
      'Vertex AI models for performance forecasting and risk detection',
      'Cloud Storage integration for high-throughput data migration pipelines',
      'Cloud Composer for orchestrated, auditable ETL workflows',
    ],
  },
  {
    slug: 'aws',
    category: 'Cloud Infrastructure',
    logoPair: {
      light: '/logos/aws-light.svg',
      dark: '/logos/aws-dark.svg',
      alt: 'Amazon Web Services',
    },
    title: 'Amazon Web Services',
    subtitle: 'Enterprise cloud backbone for uninterrupted platform delivery',
    description:
      "Euclideum's entire platform runs on AWS giving organisations carrier-grade availability, elastic compute, and global edge delivery. Every workload, from high-concurrency assessments to real-time analytics, is architected for zero single points of failure.",
    points: [
      'Elastic auto-scaling for traffic spikes up to 10× baseline',
      'Multi-AZ redundancy with automated failover and disaster recovery',
      'S3-backed encrypted object storage with versioning and audit trails',
      'CloudWatch observability with proactive incident alerting',
    ],
  },
  {
    slug: 'google-drive',
    category: 'Content & Storage',
    logo: { src: '/logos/google-drive.svg', alt: 'Google Drive' },
    title: 'Google Drive',
    subtitle: 'Content libraries migrated and synchronised automatically',
    description:
      "Organisations managing course materials, policy documents, and shared resources in Google Drive can migrate or continuously sync that content into Euclideum's unified repository. Permissions, folder hierarchy, and version history are all preserved through the transfer.",
    points: [
      'One-click bulk migration of Drive folders into the content library',
      'Bidirectional sync for shared resource libraries across teams',
      'Permission-aware transfer preserving role-based access control',
      'File metadata, version history, and timestamps retained on import',
    ],
  },
  {
    slug: 'matlab',
    category: 'Technical Computing',
    logo: { src: '/logos/matlab.svg', alt: 'MATLAB' },
    title: 'MATLAB',
    subtitle: 'Computational workflows embedded natively in the platform',
    description:
      "Euclideum's MATLAB integration brings numerical computing and simulation workflows directly into the assignment pipeline. Teams submit, execute, and receive automated feedback on MATLAB scripts entirely within the platform eliminating tool-switching and manual grading overhead.",
    points: [
      'Script submission and execution within the LMS assignment engine',
      'Automated output validation and rubric-based auto-grading',
      'Live execution logs and result visibility for reviewers',
      'Bulk import of existing MATLAB course packs and question banks',
    ],
  },
  {
    slug: 'google-docs',
    category: 'Document Workflows',
    title: 'Google Docs',
    subtitle: 'Live documents embedded and synced across the content ecosystem',
    description:
      "Policy documents, SOPs, training materials, and reference content maintained in Google Docs are imported directly into Euclideum's document repository. Live Doc links embedded in modules stay current automatically eliminating stale duplicates and manual update cycles.",
    points: [
      'Bulk import of policy, procedure, and reference documents',
      'Live Google Doc embedding within module content blocks',
      'Rich-text formatting and structure preserved on import',
      'Collaborative co-editing linked directly to published content',
    ],
  },
  {
    slug: 'vocareum',
    category: 'Lab & Assessment',
    logo: {
      src: '/images/vocareum_logo_dark.webp',
      alt: 'Vocareum',
      invert: true,
    },
    title: 'Vocareum',
    subtitle: 'Cloud lab environments and auto-assessed technical assignments',
    description:
      "Euclideum's Vocareum integration brings sandboxed cloud lab environments and auto-assessed programming assignments natively into the assignment pipeline. Administrators provision environments, teams work in isolated cloud workspaces, and scores sync back to the gradebook automatically.",
    points: [
      'Lab environment provisioning directly from the assignment engine',
      'Isolated cloud workspaces for code execution and technical tasks',
      'Auto-graded assessments with configurable scoring rubrics',
      'Full grade and progress sync back to the Euclideum gradebook',
    ],
  },
  {
    slug: 'google-sheets',
    category: 'Data Migration',
    logo: { src: '/logos/google-sheets.svg', alt: 'Google Sheets' },
    title: 'Google Sheets',
    subtitle: 'Structured data extracted, validated and loaded with zero loss',
    description:
      'Many organisations maintain critical records rosters, financial structures, schedules, and performance data in Google Sheets. Euclideum’s migration engine maps, validates, and imports this data directly into the appropriate modules, replacing fragmented spreadsheets with a single source of truth.',
    points: [
      'Personnel and roster data import with field-level validation',
      'Financial structures, fee heads, and transaction history migration',
      'Attendance, performance, and schedule bulk import at scale',
      'Real-time error flagging with guided resolution before commit',
    ],
  },
  {
    slug: 'zoom',
    category: 'Communication & Collaboration',
    logo: { src: '/logos/zoom.svg', alt: 'Zoom' },
    title: 'Zoom',
    subtitle:
      'Live sessions and virtual collaboration embedded in the platform',
    description:
      "Euclideum's Zoom integration brings live virtual sessions, training calls, and collaborative meetings directly into the platform workflow. Coordinators schedule and launch sessions without leaving the dashboard, participants join from their module page, and attendance is recorded automatically against the relevant course or program.",
    points: [
      'Session scheduling and launch directly from the module or course view',
      'One-click join links for participants with no external redirect required',
      'Automatic attendance logging synced back to the learner record',
      'Recording storage linked to the relevant course or session entry',
    ],
  },
]

const STEPS = [
  {
    step: '01',
    title: 'Discovery & Audit',
    text: 'We look at what they have now old software, spreadsheets, databases, website content, user accounts. We list what matters and what needs cleaning.',
  },
  {
    step: '02',
    title: 'Schema Mapping',
    text: "We match their data to the software we're building for them. Fields, users, content, permissions. You review and approve before anything moves.",
  },
  {
    step: '03',
    title: 'Encrypted Transfer',
    text: 'Small batches, encrypted, every record logged. If something fails, the batch stops and we tell you exactly why. No silent errors.',
  },
  {
    step: '04',
    title: 'Verify & Go-Live',
    text: 'We compare every record source vs. destination. Client checks key data themselves. They confirm. You launch. Rollback ready if needed.',
  },
]

/* One box for every mark. Fixed width AND height with object-contain, so the
   marks share a footprint and sit on a common optical line down the column —
   sizing each by its own aspect ratio made a wide wordmark dwarf a square
   glyph. `object-right` keeps them flush with the container's right edge. */
const LOGO_BOX =
  'h-[68px] w-[192px] max-w-full object-contain object-left lg:object-right'

/* ── One manifest entry ── */
function Entry({ system, index }: { system: System; index: number }) {
  const row = useReveal<HTMLDivElement>(0.12)

  return (
    <article
      ref={row.ref}
      id={system.slug}
      className={cn(
        'grid scroll-mt-24 gap-x-10 gap-y-5 border-b py-10 sm:py-12',
        'lg:grid-cols-[3.5rem_minmax(0,1fr)_13rem]',
        HAIRLINE
      )}
      style={{
        opacity: row.shown ? 1 : 0,
        transform: row.shown ? 'none' : 'translateY(10px)',
        transition: `opacity 480ms ${EASE}, transform 480ms ${EASE}`,
      }}
    >
      <p className="order-1 font-mono text-sm tabular-nums text-muted-foreground/70 lg:col-start-1 lg:row-start-1 lg:pt-1.5">
        {String(index + 1).padStart(2, '0')}
      </p>

      <div className="order-3 lg:col-start-2 lg:row-start-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600 dark:text-primary-active">
          {system.category}
        </p>

        <h2 className="font-heading mt-3 text-2xl font-normal leading-snug text-foreground sm:text-3xl">
          {system.title}
        </h2>

        <p className="mt-2 text-base text-muted-foreground sm:text-[17px]">
          {system.subtitle}
        </p>

        {/* Left-ragged, never justified — justified type at this measure opens
            rivers on every second line. */}
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {system.description}
        </p>

        <ul className="mt-6 grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
          {system.points.map((pt) => (
            <li
              key={pt}
              className="flex items-start gap-2.5 text-[15px] leading-relaxed text-muted-foreground"
            >
              <span
                aria-hidden
                className="mt-2 size-1 shrink-0 rounded-full bg-emerald-600 dark:bg-primary-active"
              />
              {pt}
            </li>
          ))}
        </ul>
      </div>

      {/* The system's own mark, sitting quietly at the end of its row like a
          letterhead. This slot held a product screenshot under a 92% black
          scrim with the title and a pulsing capsule printed on top; the marks
          say which system this is far faster than a screenshot of it does.

          All files are local under /public/logos — nothing is hotlinked. */}
      {/* Every mark gets the SAME fixed box and is contained inside it, so a
          wide wordmark (AWS) and a square glyph (Drive) occupy identical
          footprints down the column instead of each being sized by its own
          aspect ratio. */}
      <div className="order-2 flex items-start lg:col-start-3 lg:row-start-1 lg:justify-end lg:pt-1">
        {system.logoPair ? (
          <>
            <img
              src={system.logoPair.light}
              alt={system.logoPair.alt}
              loading="lazy"
              decoding="async"
              className={cn(LOGO_BOX, 'dark:hidden')}
            />
            <img
              src={system.logoPair.dark}
              alt={system.logoPair.alt}
              loading="lazy"
              decoding="async"
              className={cn(LOGO_BOX, 'hidden dark:block')}
            />
          </>
        ) : system.logo ? (
          <img
            src={system.logo.src}
            alt={system.logo.alt}
            loading="lazy"
            decoding="async"
            className={cn(
              LOGO_BOX,
              // Dark-drawn art needs flipping to read on the dark canvas.
              system.logo.invert && 'dark:brightness-0 dark:invert'
            )}
          />
        ) : null}
      </div>
    </article>
  )
}

export default function MigrationServicePage() {
  const band = useReveal<HTMLDivElement>(0.25)
  const steps = useReveal<HTMLDivElement>(0.15)

  return (
    <main className="relative bg-background">
      {/* ── Head ─────────────────────────────────────────────────────────── */}
      <section
        className={cn(
          'relative overflow-hidden hero-adaptive-bg',
          'mt-[calc(var(--header-h)*-1)]'
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: 'var(--circuit-bg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            WebkitMaskImage:
              'radial-gradient(ellipse 100% 70% at 74% 32%, #000 12%, rgba(0,0,0,0.34) 54%, transparent 90%)',
            maskImage:
              'radial-gradient(ellipse 100% 70% at 74% 32%, #000 12%, rgba(0,0,0,0.34) 54%, transparent 90%)',
          }}
        />

        <div className="container relative z-10 mx-auto">
          <div className="flex min-h-[64svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+3.5rem)] sm:pb-20">
            <Link
              href="/solutions"
              className="inline-flex w-fit items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              Solutions
            </Link>

            <div className="mt-8">
              <Eyebrow>Seamless migration &amp; integrations</Eyebrow>
            </div>

            <h1 className="font-heading mt-6 max-w-4xl text-[32px]/[1.14] font-normal tracking-tight text-foreground sm:text-[44px]/[1.12] md:text-5xl/[1.1] lg:text-[58px]/[1.08]">
              <ChromaText>
                Connect every system.{' '}
                <span className="text-emerald-600 dark:text-primary-active">
                  Migrate every record. Zero loss.
                </span>
              </ChromaText>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg md:text-[19px]">
              Euclideum connects and migrates seamlessly with AWS, Google Cloud,
              MATLAB, Google Drive, Google Sheets, Google Docs, and Vocareum so
              your organisation never skips a beat during transition.
            </p>

            {/* The page's whole purpose is the assessment, and the old build
                offered only an "All Solutions" link at the very bottom. */}
            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-7">
              <Button asChild size="xl" className="w-full sm:w-auto">
                <Link href="/schedule-meeting">
                  Book a migration assessment
                </Link>
              </Button>

              <QuietLink href="#manifest">See the connected systems</QuietLink>
            </div>
          </div>
        </div>

        {/* Scope band: rule full-bleed, cells in the container so the first
            lines up under the headline. */}
        <div ref={band.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
          <div className="container mx-auto">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 py-5 lg:grid-cols-4 lg:gap-x-px lg:gap-y-0 lg:bg-border/70 lg:py-0">
              {[
                { label: 'Connected systems', value: String(SYSTEMS.length) },
                { label: 'Records lost', value: 'Zero' },
                { label: 'Process', value: `${STEPS.length} stages` },
                { label: 'Go-live', value: 'Rollback ready' },
              ].map((f, i) => (
                <div
                  key={f.label}
                  className="lg:bg-background lg:py-6 lg:pl-5 lg:pr-6 lg:first:pl-0"
                  style={{
                    opacity: band.shown ? 1 : 0,
                    transform: band.shown ? 'none' : 'translateY(8px)',
                    transition: `opacity 460ms ${EASE} ${i * 70}ms, transform 460ms ${EASE} ${i * 70}ms`,
                  }}
                >
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                    {f.label}
                  </dt>
                  <dd className="font-heading mt-1.5 text-lg font-normal tabular-nums text-foreground sm:text-xl">
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* ── The manifest ─────────────────────────────────────────────────── */}
      <section id="manifest" className="container mx-auto scroll-mt-20">
        <div className="pt-16 sm:pt-20">
          <Eyebrow>Connected platforms</Eyebrow>
          <h2 className="font-heading mt-5 max-w-3xl text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
            <ChromaText>What comes across, system by system</ChromaText>
          </h2>
        </div>

        {/* Entries carry their own bottom rule; the list only needs a top one. */}
        <div className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
          {SYSTEMS.map((s, i) => (
            <Entry key={s.slug} system={s} index={i} />
          ))}
        </div>
      </section>

      {/* ── Process ──────────────────────────────────────────────────────── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <Eyebrow>Our process</Eyebrow>
        <h2 className="font-heading mt-5 max-w-3xl text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
          <ChromaText>
            Migration handled{' '}
            <span className="text-emerald-600 dark:text-primary-active">
              end-to-end
            </span>
          </ChromaText>
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          A structured, engineer-led process from initial audit to fully
          verified go-live.
        </p>

        {/* Four stages on one rule. The old build wrapped each in a bordered
            card with its icon in a filled square and a gradient connector
            line between them. */}
        <div
          ref={steps.ref}
          className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}
        >
          <div className="grid lg:grid-cols-4 lg:gap-x-px lg:bg-border/70">
            {STEPS.map((s, i) => (
              <div
                key={s.step}
                className={cn(
                  'border-b py-8 lg:border-b-0 lg:bg-background lg:py-10 lg:pl-6 lg:pr-8 lg:first:pl-0',
                  HAIRLINE
                )}
                style={{
                  opacity: steps.shown ? 1 : 0,
                  transform: steps.shown ? 'none' : 'translateY(10px)',
                  transition: `opacity 460ms ${EASE} ${i * 80}ms, transform 460ms ${EASE} ${i * 80}ms`,
                }}
              >
                <p className="font-mono text-sm tabular-nums text-muted-foreground/70">
                  {s.step}
                </p>
                <h3 className="font-heading mt-4 text-xl font-normal leading-snug text-foreground">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Close ────────────────────────────────────────────────────────── */}
      <section className="container mx-auto py-16 sm:py-24">
        <div className={cn('border-t pt-12 sm:pt-16', HAIRLINE)}>
          <h2 className="font-heading max-w-3xl text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
            <ChromaText>
              Ready to migrate your organisation without disruption?
            </ChromaText>
          </h2>

          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Our engineering team manages every step from system audit to go-live
            verification. Schedule a free migration assessment to receive a
            scoped plan before committing.
          </p>

          <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-7">
            <Button asChild size="xl" className="w-full sm:w-auto">
              <Link href="/schedule-meeting">Book a migration assessment</Link>
            </Button>

            <QuietLink href="/solutions">All solutions</QuietLink>
          </div>
        </div>
      </section>
    </main>
  )
}
